import React, { useMemo, useState } from "react"
import TooltipMaxLength from "../../../hooks/utils/TooltipMaxLength"
import { ExperienceSummary } from "../../teamExperiences/models/ExperienceSummaryModel"
import { defaultProfileImage } from "../../../utils/constants"
import { EXPERIENCE_STATUS } from "../../teamExperiences/models/ExperienceStatus"

type Props = {
    data: ExperienceSummary[]
}

const types = [
    { id: 1, name: 'Upcoming'},
    { id: 2, name: 'Past'},
    { id: 3, name: 'Pre-launch'},
]

const formatDateLong = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString;
        return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return dateString;
    }
};

export const ExperiencesList: React.FC<Props> = ({ data }) => {
    const [selectedOption, setSelectedOption] = useState(1);

    const stats = useMemo(() => {
        const items = data || [];
        const performed = items.filter((row: any) => {
            const st = Number(row.status);
            return st === EXPERIENCE_STATUS.PUBLISHED || st === EXPERIENCE_STATUS.PAST;
        }).length;

        const cancelled = items.filter((row: any) => {
            const st = Number(row.status);
            return st === EXPERIENCE_STATUS.REJECTED;
        }).length;

        const prices = items
            .map((row: any) => Number(row?.pricePerGuest ?? row?.price_per_guest ?? 0))
            .filter((p) => p > 0);

        const avgPrice = prices.length > 0 
            ? (prices.reduce((acc, curr) => acc + curr, 0) / prices.length).toFixed(2)
            : '0';

        return { performed, cancelled, avgPrice };
    }, [data]);

    const filteredData = useMemo(() => {
        return (data || []).filter((row: any) => {
            const st = Number(row.status);
            if (selectedOption === 1) {
                // Upcoming: Draft (0), Pending (3), Published/Active (1), Rejected (4)
                return st === EXPERIENCE_STATUS.DRAFT || st === EXPERIENCE_STATUS.PENDING || st === EXPERIENCE_STATUS.PUBLISHED || st === EXPERIENCE_STATUS.REJECTED;
            }
            if (selectedOption === 2) {
                // Past: Past (5)
                return st === EXPERIENCE_STATUS.PAST;
            }
            if (selectedOption === 3) {
                // Pre-launch: Pre-launch (2)
                return st === EXPERIENCE_STATUS.PRE_LAUNCH;
            }
            return true;
        });
    }, [data, selectedOption]);

    return (<div className="w-100 flex-column">
        <div className="member-stats mb-4 justify-content-end">
            <div className="stat-card">
                <div className="stat-card__content">
                <div className="stat-card__label">Experiences performed</div>
                <div className="stat-card__value">{stats.performed}</div>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-card__content">
                <div className="stat-card__label">Experience cancelled</div>
                <div className="stat-card__value">{stats.cancelled}</div>
                </div>
            </div>
            <div className="stat-card">
                <div className="stat-card__content">
                <div className="stat-card__label">Average price per person day</div>
                <div className="stat-card__value">${stats.avgPrice}</div>
                </div>
            </div>
        </div>
        <div className="admin-experience-details-card">
            <div className='annual-limits__options gap-3'>
                {(types || []).map((type:any,index:number)=>{
                    return (<div key={`radio-filter-experience-${index}`} className="admin-experience-details-card p-4">
                        <label className="tap-host-option w-100 w-lg-auto m-2 m-lg-0">
                            <input onChange={(e) => setSelectedOption(Number(e.target.value))}
                                type="radio"
                                name="experienceType"
                                value={Number(type.id)}
                                checked={selectedOption === Number(type.id)}
                                className="tap-host-radio-input"
                            />
                            <div className="tap-host-radio">
                                <div className="tap-host-radio-outer"></div>
                                <div className="tap-host-radio-inner"></div>
                            </div>
                            <div className="tap-host-option-label">{type.name}</div>
                        </label>
                    </div>)
                })}
            </div>
            <div className="partner-experiences-table">
                <div className="partner-experiences-table__header">
                    <div>ID</div>
                    <div>NAME</div>
                    <div>HOSTED BY</div>
                    <div>DATE</div>
                    <div>PRICE</div>
                </div>

                {filteredData.map((row: any, index: number) => {
                    const hostName = row?.host ? `${row.host.firstName || ''} ${row.host.lastName || ''}`.trim() : (row?.submittedBy?.name || 'N/A');
                    const hostImage = row?.host?.profilePicture || row?.host?.profile?.profilePicture || row?.submittedBy?.image || defaultProfileImage;
                    const dateRaw = row?.createdAt || row?.dateUploaded || row?.created_at;
                    const dateStr = formatDateLong(dateRaw);
                    const priceStr = row?.pricePerGuest != null ? `$${row.pricePerGuest}` : (row?.price_per_guest != null ? `$${row.price_per_guest}` : 'N/A');

                    return (
                        <div key={row?.id || index} className="partner-experiences-table__row">
                            <div className="partner-experiences-table__cell">
                                #{row.id}
                            </div>
                            <div className="partner-experiences-table__cell">
                                <TooltipMaxLength
                                    text={row.title || 'Untitled'}
                                    maxLength={80}
                                    maxWidth={380}
                                    position="bottom"
                                />
                            </div>
                            <div className="partner-experiences-table__cell card-profile-avatar">
                                <img src={hostImage} onError={(e) => { e.currentTarget.src = defaultProfileImage; }} alt={hostName}/>
                                <span>{hostName}</span>
                            </div>
                            <div className="partner-experiences-table__cell">
                                {dateStr}
                            </div>
                            <div className="partner-experiences-table__cell">
                                {priceStr}
                            </div>
                        </div>
                    );
                })}
                {filteredData.length === 0 && (
                    <div className="p-4 text-center text-muted">
                        No experiences available for the selected filter.
                    </div>
                )}
            </div>
        </div>
    </div>)
}